import {ChangeDetectionStrategy, Component, effect, HostBinding, input, signal} from '@angular/core';
import {QueryParams} from '../../../types/QueryParams';
import {CareService} from '../../../types/CareService';
import {BookingSettings} from '../../../types/BookingSettings';
import {CareproviderUnitType} from '../../../types/CareproviderUnitType';
import {CareproviderContactInfo} from '../../../types/CareproviderContactInfo';
import {EventActionEnum, EventCategoryEnum} from '../../../enums/GaTrackingEnums';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';
import {TimeslotDetails} from '../../../types/TimeslotDetails';
import {CareServiceToDisplay} from '../../../types/CareServiceToDisplay';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {ToolsService} from '../../../services/tools.service';
import {CareproviderUnitService} from '../../../services/careprovider-unit.service';
import * as dayjs from 'dayjs';
import {BookingTypeIdEnum} from '../../../enums/BookingEnums';

@Component({
  selector: 'app-care-service-list',
  templateUrl: './care-service-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CareServiceListComponent {
  public selectedId = input<number>(null);
  public queryParams = input<QueryParams>({});
  public careServices = input<CareService[]>(null);
  public careProviderUnitId = input<number>(null);
  public careProviderUnitUrlPath = input<string>(null);
  public careProviderUnitName = input<string>(null);
  public careProviderUnitUrl = input<string[]>(null);
  public bookingSettings = input<BookingSettings[]>(null);
  public mainCaregiverUnitType = input<CareproviderUnitType>(null);
  public careProviderContactInfo = input<CareproviderContactInfo>(null);
  public bordered = input<boolean>(true);

  public eventCategoryEnum = EventCategoryEnum;
  public eventActionEnum = EventActionEnum;
  public pageType: DLPageTypeEnum = DLPageTypeEnum.CARE_SERVICE_PAGE;
  public careServicesTimeSlots = signal<Array<Array<TimeslotDetails> | null>>([]);
  public careServicesToDisplay = signal<CareServiceToDisplay[]>([]);

  constructor(
    private urlBuilder: UrlBuilderService,
    private tools: ToolsService,
    private careProviderUnitService: CareproviderUnitService,
  ) {
    dayjs.locale('sv');

    effect(
      () => {
        if (this.careServices()) {
          this.prepareComponentData();
        }
      },
      {allowSignalWrites: true},
    );
  }

  @HostBinding('class')
  get hostClasses(): string {
    const baseClasses = 'block';
    const conditionalClasses =
      this.bordered() === true ? 'md:rounded-lg md:border md:border-grey-9 md:bg-white md:px-4 md:py-5' : '';

    return `${baseClasses} ${conditionalClasses}`.trim();
  }

  private getCareServiceRoutePath(careService: CareService): string[] {
    return this.urlBuilder.CreateCareServiceRouterLink(
      this.careProviderUnitId(),
      this.careProviderUnitUrlPath(),
      careService.id,
      careService.urlPath,
    );
  }

  private getElCssClass(baseCssClass: string, index: number): string {
    return !this.tools.isNil(index) ? `${baseCssClass}-${index}` : `${baseCssClass}`;
  }

  private prepareComponentData(): void {
    this.careServicesTimeSlots.set(
      this.careServices()
        .filter((item) => item.id !== this.selectedId())
        .map((careService: CareService): TimeslotDetails[] | null => {
          if (
            careService.timeSlots == undefined ||
            careService.timeSlots.length == 0 ||
            careService.careServiceBookingSettings.bookingType.id !== BookingTypeIdEnum.VARDEN
          ) {
            return null;
          }
          const timeslots: TimeslotDetails[] = [];
          careService.timeSlots.forEach((timeslot) => {
            timeslots.push(
              this.careProviderUnitService.genTimeslotDetails(careService.id, timeslot, this.careProviderUnitId()),
            );
          });
          return timeslots;
        }),
    );

    this.careServicesToDisplay.set(
      this.careServices()
        .filter((item) => item.id !== this.selectedId())
        .map((careService: CareService, index: number) => {
          return {
            ...careService,
            cssClass: this.getElCssClass('test-care-service-item', index),
            isInternal: this.careProviderUnitService.isInternalCareService(careService, this.bookingSettings()),
            dataLayerParams: this.careProviderUnitService.getDataLayerEventParams(
              careService,
              index,
              this.careProviderUnitId(),
              this.mainCaregiverUnitType()?.id,
              this.mainCaregiverUnitType()?.area?.id,
              this.pageType,
            ),
            routerLink: this.getCareServiceRoutePath(careService),
          };
        }),
    );
  }
}
